'use client'

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { Button, toast, useField, useFormFields } from '@payloadcms/ui'
import { useCallback, useEffect, useRef, useState } from 'react'

import styles from './FindObjectEditor.module.scss'

import type { DragMoveEvent } from '@dnd-kit/core'
import type { FindItemInThePictureGameTarget } from '@ruslo/types'
import type { GroupFieldClientProps } from 'payload'
import type { CSSProperties, FC, PointerEvent } from 'react'

type ResizeCorner = 'tl' | 'tr' | 'bl' | 'br'

type ImageState = {
  height: number | null
  url: string
  width: number | null
}

type ResizeState = {
  corner: ResizeCorner
  pointerX: number
  pointerY: number
  target: FindItemInThePictureGameTarget
}

type DraggableTargetProps = {
  imageHeight: number
  imageWidth: number
  target: FindItemInThePictureGameTarget
  onResizeStart?: (
    corner: ResizeCorner,
    event: PointerEvent<HTMLDivElement>,
  ) => void
}

const MIN_TARGET_SIZE = 20

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max))

const DraggableTarget: FC<DraggableTargetProps> = ({
  imageHeight,
  imageWidth,
  target,
  onResizeStart,
}) => {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: 'target',
  })
  const style: CSSProperties = {
    height: `${(target.height / imageHeight) * 100}%`,
    left: `${(target.x / imageWidth) * 100}%`,
    top: `${(target.y / imageHeight) * 100}%`,
    width: `${(target.width / imageWidth) * 100}%`,
  }

  const handleResizePointerDown =
    (corner: ResizeCorner) => (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget.setPointerCapture(event.pointerId)
      onResizeStart?.(corner, event)
    }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={styles.target}
      style={style}
    >
      <div className={styles.targetBorder}>
        {(['tl', 'tr', 'bl', 'br'] as ResizeCorner[]).map((corner) => (
          <div
            key={corner}
            className={`${styles.targetCorner} ${styles[corner]}`}
            onPointerDown={handleResizePointerDown(corner)}
          />
        ))}
      </div>
    </div>
  )
}

const FindObjectEditor: FC<GroupFieldClientProps> = ({ path }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const resizeStateRef = useRef<ResizeState | null>(null)
  const { value, setValue } = useField<FindItemInThePictureGameTarget>({ path })
  const imageField = useFormFields(([fields]) => {
    const pathParts = path.split('.')
    const parentPath = pathParts.slice(0, -1).join('.')

    return fields[`${parentPath}.image`]
  })
  const [target, setTarget] = useState<{
    initialTarget: FindItemInThePictureGameTarget
    target: FindItemInThePictureGameTarget
  }>({
    initialTarget: value,
    target: value,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [image, setImage] = useState<ImageState | null>(null)
  const [isResizing, setIsResizing] = useState<boolean>(false)
  const mouseSensor = useSensor(MouseSensor)
  const touchSensor = useSensor(TouchSensor)
  const keyboardSensor = useSensor(KeyboardSensor, {})
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor)
  const imageWidth = image?.width ?? null
  const imageHeight = image?.height ?? null
  const getStageScale = useCallback(() => {
    if (!containerRef.current || !imageWidth || !imageHeight) {
      return { x: 1, y: 1 }
    }

    const rect = containerRef.current.getBoundingClientRect()

    return {
      x: rect.width ? rect.width / imageWidth : 1,
      y: rect.height ? rect.height / imageHeight : 1,
    }
  }, [imageWidth, imageHeight])

  const handleSave = () => {
    setValue(target.target)
    toast.success('Объект обновлен')
  }

  const handleResizeStart = (
    corner: ResizeCorner,
    event: PointerEvent<HTMLDivElement>,
  ) => {
    resizeStateRef.current = {
      corner,
      pointerX: event.clientX,
      pointerY: event.clientY,
      target: target.target,
    }
    setIsResizing(true)
  }

  const handleDragMove = (event: DragMoveEvent) => {
    if (!imageWidth || !imageHeight || isResizing) return

    setTarget(({ initialTarget, target }) => {
      const scale = getStageScale()
      let x = initialTarget.x + event.delta.x / scale.x
      let y = initialTarget.y + event.delta.y / scale.y

      x = clamp(x, 0, imageWidth - target.width)
      y = clamp(y, 0, imageHeight - target.height)

      return {
        initialTarget,
        target: { ...target, x, y },
      }
    })
  }

  const handleDragEnd = () => {
    if (isResizing) return

    setTarget(({ target }) => ({
      target,
      initialTarget: target,
    }))
  }

  const handleDragCancel = () => {
    if (isResizing) return

    setTarget(({ initialTarget }) => ({
      initialTarget,
      target: initialTarget,
    }))
  }

  useEffect(() => {
    if (!imageField.value) return

    const load = async () => {
      try {
        setIsLoading(true)

        const res = await fetch(`/api/media/${imageField.value}`)
        const json = await res.json()

        setImage({
          height: json.height ?? null,
          url: json.url,
          width: json.width ?? null,
        })
      } finally {
        setIsLoading(false)
      }
    }

    void load()
  }, [imageField])

  useEffect(() => {
    if (!imageWidth || !imageHeight) return

    setTarget(({ initialTarget, target }) => {
      const width = clamp(target.width, MIN_TARGET_SIZE, imageWidth)
      const height = clamp(target.height, MIN_TARGET_SIZE, imageHeight)
      const x = clamp(target.x, 0, imageWidth - width)
      const y = clamp(target.y, 0, imageHeight - height)
      const nextTarget = { x, y, width, height }

      return {
        initialTarget,
        target: nextTarget,
      }
    })
  }, [imageWidth, imageHeight])

  useEffect(() => {
    if (!isResizing || !imageWidth || !imageHeight) return

    const handlePointerMove = (event: globalThis.PointerEvent) => {
      const resizeState = resizeStateRef.current

      if (!resizeState) return

      const scale = getStageScale()
      const deltaX = (event.clientX - resizeState.pointerX) / scale.x
      const deltaY = (event.clientY - resizeState.pointerY) / scale.y
      const { corner, target: startTarget } = resizeState
      let x = startTarget.x
      let y = startTarget.y
      let width = startTarget.width
      let height = startTarget.height

      if (corner === 'tl' || corner === 'bl') {
        const right = startTarget.x + startTarget.width

        x = clamp(startTarget.x + deltaX, 0, right - MIN_TARGET_SIZE)
        width = right - x
      } else {
        width = clamp(
          startTarget.width + deltaX,
          MIN_TARGET_SIZE,
          imageWidth - x,
        )
      }

      if (corner === 'tl' || corner === 'tr') {
        const bottom = startTarget.y + startTarget.height

        y = clamp(startTarget.y + deltaY, 0, bottom - MIN_TARGET_SIZE)
        height = bottom - y
      } else {
        height = clamp(
          startTarget.height + deltaY,
          MIN_TARGET_SIZE,
          imageHeight - y,
        )
      }

      setTarget(({ initialTarget }) => ({
        initialTarget,
        target: { x, y, width, height },
      }))
    }

    const handlePointerUp = () => {
      resizeStateRef.current = null
      setIsResizing(false)
      setTarget(({ target }) => ({
        initialTarget: target,
        target,
      }))
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [isResizing, imageWidth, imageHeight, getStageScale])

  if (!image?.url) return <div>{isLoading ? 'Loading...' : 'Upload image'}</div>

  return (
    <div className={styles.root}>
      <Button type={'button'} onClick={handleSave}>
        Обновить объект
      </Button>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragEnd}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className={styles.stageViewport}>
          <div className={styles.container}>
            <div ref={containerRef} className={styles.stage}>
              {/* biome-ignore lint/performance/noImgElement: The editor needs natural image dimensions when Payload metadata is missing. */}
              <img
                alt={''}
                className={styles.image}
                draggable={false}
                onLoad={(event) => {
                  const { naturalHeight, naturalWidth } = event.currentTarget

                  setImage((currentImage) =>
                    currentImage
                      ? {
                          ...currentImage,
                          height: currentImage.height ?? naturalHeight,
                          width: currentImage.width ?? naturalWidth,
                        }
                      : currentImage,
                  )
                }}
                src={image.url}
              />
              {imageWidth && imageHeight && (
                <DraggableTarget
                  imageHeight={imageHeight}
                  imageWidth={imageWidth}
                  target={target.target}
                  onResizeStart={handleResizeStart}
                />
              )}
            </div>
          </div>
        </div>
      </DndContext>
    </div>
  )
}

export default FindObjectEditor
